/**
 * Hook for new revision info (api/get-newrevisioninfo).
 * All state lives in slice: isViewCopyRequired, isWord, selectedRevisionItem, newRevision, etc.
 */
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  fetchNewRevisionInfoThunk,
  clearNewRevisionInfo,
  setNewRevisionText,
  setCreateViewCopy,
  setNewRevision,
  setValidationError,
} from "../slices/documents/newRevisionInfoSlice";
import { DOC_CHECK_IN_TYPE } from "../constants/metaData";

function selectNewRevisionInfo(state) {
  return state.NewRevisionInfo ?? {};
}

export function useNewRevisionInfo() {
  const dispatch = useAppDispatch();
  const slice = useAppSelector(selectNewRevisionInfo);

  const fetchNewRevisionInfo = useCallback(
    (selectedRow, options = {}) => {
      const {
        requestDocRevInfo = false,
        documentCheckInType = DOC_CHECK_IN_TYPE.checkinNewRevision,
        uploadFiles = [],
        uploadViewCopyCount = 0,
      } = options;
      return dispatch(
        fetchNewRevisionInfoThunk({
          selectedRow,
          requestDocRevInfo,
          documentCheckInType,
          uploadFiles,
          uploadViewCopyCount,
        })
      );
    },
    [dispatch]
  );

  const clear = useCallback(() => dispatch(clearNewRevisionInfo()), [dispatch]);
  const onNewRevisionTextChange = useCallback(
    (text) => dispatch(setNewRevisionText(text)),
    [dispatch]
  );
  const onCreateViewCopySelect = useCallback(
    (checked) => dispatch(setCreateViewCopy(checked)),
    [dispatch]
  );
  const onNewRevisionChange = useCallback(
    (value) => dispatch(setNewRevision(value)),
    [dispatch]
  );
  const onValidationError = useCallback(
    (message) => dispatch(setValidationError(message)),
    [dispatch]
  );

  return {
    fetchNewRevisionInfo,
    clearNewRevisionInfo: clear,
    data: slice.data,
    isLoading: slice.isLoading,
    isError: slice.isError,
    message: slice.message,
    isViewCopyRequired: slice.isViewCopyRequired,
    isFormatRequired: slice.isFormatRequired,
    isWord: slice.isWord,
    docClassFormatList: slice.docClassFormatList,
    fileTypes: slice.fileTypes,
    createViewCopy: slice.createViewCopy,
    createViewCopyDisable: slice.createViewCopyDisable,
    selectedRevisionItem: slice.selectedRevisionItem,
    newRevision: slice.newRevision,
    newRevisionText: slice.newRevisionText,
    newRevisionObjCon: slice.newRevisionObjCon,
    newRevisionDisable: slice.newRevisionDisable,
    hasData: slice.data != null,
    validationError: slice.validationError,
    onNewRevisionTextChange,
    onCreateViewCopySelect,
    onNewRevisionChange,
    onValidationError,
  };
}
